import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { MessageSquare, Users, Sparkles, Lock, Copy, ArrowRight, Mail, Share2 } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import type { Decision } from '../types/decision';
import Analytics from '../services/analytics';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";


export function ShareDecisionPage() {
  const { decisionId } = useParams();
  const navigate = useNavigate();
  const [decision, setDecision] = useState<Decision | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Check for mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load decision data
  useEffect(() => {
    const loadDecision = async () => {
      if (!decisionId) return;
      
      try {
        const decisionRef = doc(db, 'decisions', decisionId);
        const decisionSnap = await getDoc(decisionRef);

        if (!decisionSnap.exists()) {
          throw new Error('Decision not found');
        }

        setDecision({
          id: decisionSnap.id,
          ...decisionSnap.data()
        } as Decision);
      } catch (err) {
        console.error('Error loading decision:', err);
        setError('Failed to load decision details');
      } finally {
        setLoading(false);
      }
    };

    loadDecision();
  }, [decisionId]);

  const decisionUrl = `${window.location.origin}/d/${decisionId}/chat`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(decisionUrl);
      Analytics.trackDecisionShared(decisionId!, 'copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Failed to copy link');
    }
  };

  const handleEmailShare = (app?: 'gmail' | 'default') => {
    const subject = encodeURIComponent(`Help us decide: ${decision?.question}`);
    const body = encodeURIComponent(
      `I'd like your input on a group decision:\n\n` +
      `Question: ${decision?.question}\n` +
      (decision?.details ? `\nDetails: ${decision.details}\n\n` : '\n\n') +
      `Share your input here: ${decisionUrl}`
    );
    
    Analytics.trackDecisionShared(decisionId!, app === 'gmail' ? 'gmail' : 'email');

    if (app === 'gmail') {
      window.open(`https://mail.google.com/mail/u/0/?fs=1&tf=cm&subject=${subject}&body=${body}`);
    } else {
      window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {

        Analytics.trackDecisionShared(decisionId!, 'native');

        await navigator.share({
          title: `Group Decision: ${decision?.question}`,
          text: `Help us decide: ${decision?.question}`,
          url: decisionUrl
        });
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError('Failed to share');
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto max-w-3xl">
        <Card>
          <CardContent className="p-8">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !decision) {
    return (
      <div className="container mx-auto max-w-3xl">
        <Alert variant="destructive">
          <AlertDescription>{error || 'Decision not found'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const hasWebShareApi = isMobile && typeof navigator !== 'undefined' && 'share' in navigator;

  return (
    <div className="container mx-auto max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Invite Your Group</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Decision Preview */}
          <div className="rounded-lg border bg-card p-6">
            <h3 className="font-semibold mb-2">{decision.question}</h3>
            
            {decision.details && (
              <div>
                {showDetails ? (
                  <>
                    <p className="text-muted-foreground">{decision.details}</p>
                    <button 
                      onClick={() => setShowDetails(false)}
                      className="text-sm text-primary hover:underline mt-2 focus:outline-none"
                    >
                      Hide details
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setShowDetails(true)}
                    className="text-sm text-primary hover:underline focus:outline-none"
                  >
                    Show details
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Share Options */}
          <div className="space-y-3">
            {hasWebShareApi ? (
              <>
                <Button 
                  className="w-full"
                  onClick={handleNativeShare}
                >
                  <Share2 className="h-5 w-5 mr-2" />
                  Share with your group
                </Button>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="outline"
                    onClick={handleCopyLink}
                  >
                    <Copy className="h-5 w-5 mr-2" />
                    {copied ? 'Copied!' : 'Copy link'}
                  </Button>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline">
                        <Mail className="h-5 w-5 mr-2" />
                        Share via email
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-white shadow-lg rounded-md min-w-[180px] z-50" align="end" sideOffset={5}>
                      <DropdownMenuItem onClick={() => handleEmailShare('gmail')}>
                        Open in Gmail
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEmailShare('default')}>
                        Open in default email app
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </>
            ) : (
              <>
                <Button 
                  className="w-full"
                  onClick={handleCopyLink}
                >
                  <Copy className="h-5 w-5 mr-2" />
                  {copied ? 'Copied!' : 'Copy link'}
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      className="w-full"
                      variant="outline"
                    >
                      <Mail className="h-5 w-5 mr-2" />
                      Share via email
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => handleEmailShare('gmail')}>
                      Open in Gmail
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleEmailShare('default')}>
                      Open in default email app
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium">What happens next</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <MessageSquare className="h-7 w-7 text-primary flex-shrink-0" />
                <p>Each participant will have a private conversation about their preferences</p>
              </div>
              <div className="flex items-start gap-4">
                <Users className="h-7 w-7 text-primary flex-shrink-0" />
                <p>You'll be able to see who's participated</p>
              </div>
              <div className="flex items-start gap-4">
                <Sparkles className="h-7 w-7 text-primary flex-shrink-0" />
                <p>After sharing their input & ideas, participants can view the AI-generated group summary</p>
                </div>
              <div className="flex items-start gap-4">
                <Lock className="h-7 w-7 text-primary flex-shrink-0" />
                <p>The organizer closes the decision and generates a final summary with our most advanced AI</p>
              </div>
            </div>
          </div>

          <div className="pt-6">
            <Button 
              className="w-full"
              onClick={() => navigate(`/d/${decisionId}/chat`)}
            >
              View Response & Participate
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}